import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import LoadingSpinner from '../components/LoadingSpinner';
import Comments from '../components/Comments';
import { RodImage } from '../components/GameImages';
import '../css.css';
import { Plus, X } from 'lucide-react';

// 로드 테이블 뷰 컴포넌트
const RodTableView = React.memo(({ rods, rodSizes, searchTerm }) => {
    const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
    const [expandedBonuses, setExpandedBonuses] = useState({});

    const toggleBonusExpansion = (itemKey) => {
        setExpandedBonuses(prev => ({
            ...prev,
            [itemKey]: !prev[itemKey]
        }));
    };

    // 정렬 처리
    const handleSort = (key) => {
        let direction = 'asc';
        if (sortConfig.key === key && sortConfig.direction === 'asc') {
            direction = 'desc';
        }
        setSortConfig({ key, direction });
    };

    // 로드 크기별로 데이터 확장
    const expandedData = useMemo(() => {
        const expanded = [];
        
        // 보너스 필드 목록 (DB 실제 컬럼명과 일치해야 함)
        const bonusFields = [
            '찌 낚시 기술', '스핀 낚시 기술', '바텀 낚시 기술', '해양 낚시 기술', '경험치', '캐스팅 거리',
            '매치 낚싯대로 낚시하기', '피더 낚싯대 낚시', '카프 낚싯대로 낚시하기', '저크베이트 낚싯대로 낚시하기',
            '고정줄 채비 사용', '슬라이더 채비 사용', '볼로네제 채비 사용', '매치 채비 사용', '카프헤어 채비 사용',
            '스피너와 스피너베이트로 낚시하기', '스푼으로 낚시하기', '워블러로 낚시하기', '지깅',
            '쓰리웨이 채비 사용', '텍사스 채비 사용', '드롭샷 채비 사용', '캐롤라이나 채비 사용', '와키 채비 사용',
            '저크베이트 채비 사용', '인라인 채비 사용', '파터노스터 채비 사용', '루프 채비 사용',
            '클래식 헤어 채비 사용', '눈사람 채비 사용', '클래식 팝업 채비 사용', '힌지드 스티프 채비 사용',
            '초드 채비 사용', '구더기 스냅인 채비 사용', '클래식 메소드 채비 사용', '팝업 메소드 채비 사용',
            '지그-리그 채비 사용', '필커 지그', '마린 루어 지깅 채비', '필렛 지깅 채비', '플라잉 칼러 채비',
            '바텀 생미끼 채비', '자이언트 실리콘 지깅 채비', '자이언트 생미끼 지깅 채비', '마린 파터노스터 채비'
        ];
        
        rods.forEach(rod => {
            const relatedSizes = rodSizes.filter(size => size.rod_id === rod.id);
            
            if (relatedSizes.length > 0) {
                relatedSizes.forEach(size => {
                    // 디버깅을 위한 로그 (Advent CST-F66L인 경우)
                    if (rod.displayname && rod.displayname.includes('Advent CST-F66L')) {
                        console.log('Advent CST-F66L size data:', size);
                        console.log('경험치 value:', size['경험치']);
                        console.log('All bonus fields in size:', Object.keys(size).filter(key => bonusFields.includes(key)));
                    }
                    
                    // 디버깅을 위한 로그 (Crimson Star인 경우)
                    if (rod.displayname && rod.displayname.includes('Crimson Star')) {
                        console.log('Crimson Star size data:', size);
                        console.log('All bonus fields in size:', Object.keys(size).filter(key => bonusFields.includes(key)));
                        bonusFields.forEach(field => {
                            const value = size[field];
                            if (value && parseFloat(value) > 0) {
                                console.log(`${field}: ${value}`);
                            }
                        });
                    }
                    
                    // 보너스 정보 생성
                    const bonuses = [];
                    bonusFields.forEach(field => {
                        const value = size[field];
                        if (value && parseFloat(value) > 0) {
                            // 필드명을 간단하게 변환
                            let displayName = field;
                            if (field === '경험치' || field === '캐스팅 거리') {
                                bonuses.push(`${displayName} +${parseInt(value)}%`);
                            } else {
                                bonuses.push(`${displayName} +${parseInt(value)}`);
                            }
                        }
                    });
                    
                    expanded.push({
                        ...rod,
                        ...size,
                        rod_name: rod.displayname,
                        manufacturer: rod.manufacturer,
                        rod_type: rod.rod_type,
                        bonuses: bonuses.join(', ') || '없음'
                    });
                });
            } else {
                // 크기 정보가 없는 경우 기본 로드 정보만 표시
                expanded.push({
                    ...rod,
                    rod_name: rod.displayname,
                    manufacturer: rod.manufacturer,
                    rod_type: rod.rod_type,
                    size_name: '정보 없음',
                    bonuses: '없음'
                });
            }
        });
        
        return expanded;
    }, [rods, rodSizes]);

    // 검색 및 정렬된 데이터
    const filteredAndSortedData = useMemo(() => {
        let filtered = expandedData;

        // 타입 순서 정의
        const typeOrder = [
            '텔레스코픽', '볼로네제', '매치', '스피닝', '캐스팅', 
            '저크', '피더', '피커', '카프', '필커', '보트'
        ];

        // 기본 정렬: 타입순 -> 이름순
        filtered.sort((a, b) => {
            // 타입 순서로 먼저 정렬
            const aTypeIndex = typeOrder.indexOf(a.rod_type) !== -1 ? typeOrder.indexOf(a.rod_type) : 999;
            const bTypeIndex = typeOrder.indexOf(b.rod_type) !== -1 ? typeOrder.indexOf(b.rod_type) : 999;
            
            if (aTypeIndex !== bTypeIndex) {
                return aTypeIndex - bTypeIndex;
            }
            
            // 타입이 같으면 이름순으로 정렬
            const aName = a.size_name || '';
            const bName = b.size_name || '';
            return aName.localeCompare(bName, 'ko', { numeric: true });
        });

        // 사용자가 컬럼 정렬을 클릭한 경우
        if (sortConfig.key) {
            filtered.sort((a, b) => {
                let aVal = a[sortConfig.key];
                let bVal = b[sortConfig.key];

                // 숫자 값 처리
                if (typeof aVal === 'string' && !isNaN(parseFloat(aVal))) {
                    aVal = parseFloat(aVal) || 0;
                }
                if (typeof bVal === 'string' && !isNaN(parseFloat(bVal))) {
                    bVal = parseFloat(bVal) || 0;
                }

                // null, undefined, 빈 문자열 처리
                if (!aVal && aVal !== 0) aVal = '';
                if (!bVal && bVal !== 0) bVal = '';

                if (aVal < bVal) {
                    return sortConfig.direction === 'asc' ? -1 : 1;
                }
                if (aVal > bVal) {
                    return sortConfig.direction === 'asc' ? 1 : -1;
                }
                return 0;
            });
        }

        return filtered;
    }, [expandedData, sortConfig]);

    const columns = [
        { key: 'rod_type', label: '타입', sortable: true },
        { key: 'size_name', label: '이름', sortable: true },
        { key: 'sensitivity', label: '감도', sortable: true },
        { key: 'action_type', label: '액션', sortable: true },
        { key: 'strength', label: '강도', sortable: true },
        { key: 'power', label: '파워', sortable: true },
        { key: 'length', label: '길이', sortable: true },
        { key: 'test_range', label: '테스트', sortable: true },
        { key: 'max_load', label: '최대 하중', sortable: true },
        { key: 'weight', label: '무게', sortable: true },
        { key: 'bonuses', label: '보너스', sortable: false },
        { key: 'price_silver', label: '가격(실버)', sortable: true },
        { key: 'price_gold', label: '가격(골드)', sortable: true }
    ];

    return (
        <div className="table-view-container">
            <style>{`
                .has-bonus {
                    color: #007bff !important;
                    font-weight: bold !important;
                }
                td.has-bonus {
                    color: #007bff !important;
                    font-weight: bold !important;
                }
                .bonus-expanded {
                    display: flex;
                    flex-direction: column;
                    align-items: flex-start;
                }
            `}</style>
            {/* 테이블 */}
            <div className="table-container">
                <table className="data-table">
                    <thead>
                        <tr>
                            {columns.map(column => (
                                <th 
                                    key={column.key}
                                    className={column.sortable ? 'sortable' : ''}
                                    onClick={() => column.sortable && handleSort(column.key)}
                                >
                                    {column.label}
                                    {column.sortable && (
                                        <span className="sort-indicator">
                                            {sortConfig.key === column.key ? (
                                                sortConfig.direction === 'asc' ? ' ▲' : ' ▼'
                                            ) : ' ↕'}
                                        </span>
                                    )}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {filteredAndSortedData.map((item, index) => {
                            const itemKey = `${item.rod_id || item.id}-${item.size_name}-${index}`;
                            const bonusArray = (item.bonuses && item.bonuses !== '없음' && item.bonuses.trim() !== '') ? item.bonuses.split(', ') : [];
                            const showMoreButton = bonusArray.length > 1;
                            const isExpanded = expandedBonuses[itemKey];
                            
                            return (
                                <tr key={itemKey}>
                                    <td style={{ paddingLeft: '12px' }}>{item.rod_type || '정보 없음'}</td>
                                    <td className={bonusArray.length > 0 ? 'has-bonus' : ''}>
                                        {item.size_name || '정보 없음'}
                                    </td>
                                    <td>{item.sensitivity ? `${item.sensitivity}/10` : '정보 없음'}</td>
                                    <td>{item.action_type || '정보 없음'}</td>
                                    <td>{item.strength ? `${item.strength}/10` : '정보 없음'}</td>
                                    <td>{item.power || '정보 없음'}</td>
                                    <td>{item.length ? `${item.length} m` : '정보 없음'}</td>
                                    <td>
                                        {item.min_test && item.max_test 
                                            ? `${item.min_test}-${item.max_test} g`
                                            : item.min_test 
                                                ? `${item.min_test} g`
                                                : item.max_test
                                                    ? `${item.max_test} g`
                                                    : '정보 없음'
                                        }
                                    </td>
                                    <td>{item.max_load ? `${item.max_load} kg` : '정보 없음'}</td>
                                    <td>{item.weight ? `${item.weight} g` : '정보 없음'}</td>
                                    <td className="bonuses-cell">
                                        {(!item.bonuses || item.bonuses === '없음' || item.bonuses.trim() === '') ? (
                                            <span className="no-bonus">-</span>
                                        ) : (
                                            <div className="bonus-container">
                                                {isExpanded ? (
                                                    <div className="bonus-expanded">
                                                        {bonusArray.map((bonus, bonusIndex) => (
                                                            <div key={bonusIndex} className="bonus-item">
                                                                {bonus.trim()}
                                                            </div>
                                                        ))}
                                                        {showMoreButton && (
                                                            <button 
                                                                className="bonus-toggle-btn small"
                                                                onClick={() => toggleBonusExpansion(itemKey)}
                                                                style={{
                                                                    fontSize: '11px',
                                                                    padding: '2px 6px',
                                                                    minHeight: 'auto',
                                                                    lineHeight: '1.2'
                                                                }}
                                                            >
                                                                접기
                                                            </button>
                                                        )}
                                                    </div>
                                                ) : (
                                                    <div className="bonus-compact" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                        <span className="bonus-list">{bonusArray[0]}</span>
                                                        {showMoreButton && (
                                                            <button 
                                                                className="bonus-toggle-btn small"
                                                                onClick={() => toggleBonusExpansion(itemKey)}
                                                                style={{
                                                                    fontSize: '11px',
                                                                    padding: '2px 6px',
                                                                    minHeight: 'auto',
                                                                    lineHeight: '1.2'
                                                                }}
                                                            >
                                                                +{bonusArray.length - 1}개
                                                            </button>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </td>
                                    <td className="price-cell">
                                        {item.price_silver ? `${item.price_silver.toLocaleString()} 실버` : '정보 없음'}
                                    </td>
                                    <td className="price-cell">
                                        {item.price_gold ? `${item.price_gold.toLocaleString()} 골드` : '정보 없음'}
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            {filteredAndSortedData.length === 0 && (
                <div className="no-data">
                    <p>표시할 데이터가 없습니다.</p>
                </div>
            )}
        </div>
    );
});

// 낚싯대 상세 모달 컴포넌트
const RodModal = React.memo(({ rod, onClose, onAddToCompare, onRemoveFromCompare, compareList = [] }) => {
  const handleImageError = useCallback(() => {
    console.warn('Failed to load rod detail image:', rod.filename);
  }, [rod?.filename]);

  if (!rod) return null;

  // 이미 비교 목록에 있는 크기인지 확인하는 함수
  const isInCompareList = (rodId, sizeName) => {
    return compareList.some(item => item.rodId === rodId && item.sizeName === sizeName);
  };

  // 보너스가 있는지 확인하는 함수
  const hasAnyBonus = (size) => {
    const bonusFields = [
      '찌 낚시 기술', '스핀 낚시 기술', '바텀 낚시 기술', '해양 낚시 기술', '경험치', '캐스팅 거리',
      '매치 낚싯대로 낚시하기', '피더 낚싯대 낚시', '카프 낚싯대로 낚시하기', 
      '저크베이트 낚싯대로 낚시하기', '지깅',
      '고정줄 채비 사용', '슬라이더 채비 사용', '볼로네제 채비 사용', 
      '매치 채비 사용', '카프헤어 채비 사용',
      '스피너와 스피너베이트로 낚시하기', '스푼으로 낚시하기', '워블러로 낚시하기',
      '쓰리웨이 채비 사용', '텍사스 채비 사용', '드롭샷 채비 사용', 
      '캐롤라이나 채비 사용', '와키 채비 사용', '저크베이트 채비 사용', '인라인 채비 사용',
      '파터노스터 채비 사용', '루프 채비 사용', '클래식 헤어 채비 사용', 
      '눈사람 채비 사용', '클래식 팝업 채비 사용', '힌지드 스티프 채비 사용', 
      '초드 채비 사용', '구더기 스냅인 채비 사용', '클래식 메소드 채비 사용', 
      '팝업 메소드 채비 사용', '지그-리그 채비 사용',
      '필커 지그', '마린 루어 지깅 채비', '필렛 지깅 채비', '플라잉 칼러 채비', 
      '바텀 생미끼 채비', '자이언트 실리콘 지깅 채비', '자이언트 생미끼 지깅 채비', 
      '마린 파터노스터 채비'
    ];
    
    return bonusFields.some(field => {
      const value = size[field];
      return value !== null && value !== undefined && value !== '' && parseFloat(value) > 0;
    });
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content large" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{rod.displayname}</h2>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        <div className="modal-body">
          <div className="rod-detail-container">
            <div className="rod-detail-main">
              <div className="rod-image-section">
                <RodImage 
                  filename={rod.filename}
                  alt={rod.displayname}
                  className="rod-detail-image"
                  onError={handleImageError}
                />
              </div>
              <div className="rod-info-section">
                <h3>{rod.displayname}</h3>
                <div className="rod-basic-info">
                  <p><strong>제조사:</strong> {rod.manufacturer}</p>
                  <p><strong>타입:</strong> {rod.rod_type}</p>
                  {rod.note && <p><strong>비고:</strong> {rod.note}</p>}
                </div>
              </div>
            </div>
            
            {/* 크기별 상세 정보 */}
            <div className="rod-sizes-section">
              <h3>크기별 상세 정보</h3>
              <div className="sizes-grid">
                {rod.sizes && rod.sizes.map((size, index) => (
                  <div key={index} className="size-card">
                    <div className="size-header">
                      <h4 className={hasAnyBonus(size) ? 'has-bonus' : ''}>{size.이름}</h4>
                      <button
                        className={`compare-add-btn ${isInCompareList(rod.id, size.이름) ? 'remove' : 'add'}`}
                        onClick={() => {
                          if (isInCompareList(rod.id, size.이름)) {
                            onRemoveFromCompare(rod.id, size.이름);
                          } else {
                            onAddToCompare(rod, size);
                          }
                        }}
                        title={isInCompareList(rod.id, size.이름) ? '비교에서 제거' : '비교에 추가'}
                      >
                        {isInCompareList(rod.id, size.이름) ? <X className="w-3 h-3" color="white" /> : <Plus className="w-3 h-3" color="white" />}
                      </button>
                    </div>
                    <div className="size-specs">
                      <div className="spec-group">
                        {size.감도 && <p><strong>감도:</strong> {size.감도}/10</p>}
                        {size.액션 && <p><strong>액션:</strong> {size.액션}</p>}
                        {size.강도 && <p><strong>강도:</strong> {size.강도}/10</p>}
                        {size.파워 && <p><strong>파워:</strong> {size.파워}</p>}
                      </div>
                      <div className="spec-group">
                        {size['최소 테스트'] && size['최대 테스트'] && (
                          <p><strong>테스트:</strong> {size['최소 테스트']}g - {size['최대 테스트']}g</p>
                        )}
                        {size.길이 && <p><strong>길이:</strong> {size.길이}m</p>}
                        {size['최대 하중'] && <p><strong>최대 하중:</strong> {size['최대 하중']}kg</p>}
                        {size.무게 && <p><strong>무게:</strong> {size.무게}g</p>}
                        
                        {/* 보너스 항목들 */}
                        {(() => {
                          const bonuses = [];
                          
                          // 경험치 보너스
                          if (size.경험치) bonuses.push({ label: '경험치', value: `+${parseInt(size.경험치)}%` });
                          
                          // 낚시 기술 보너스
                          if (size['찌 낚시 기술']) bonuses.push({ label: '찌 낚시 기술', value: `+${parseInt(size['찌 낚시 기술'])}` });
                          if (size['스핀 낚시 기술']) bonuses.push({ label: '스핀 낚시 기술', value: `+${parseInt(size['스핀 낚시 기술'])}` });
                          if (size['바텀 낚시 기술']) bonuses.push({ label: '바텀 낚시 기술', value: `+${parseInt(size['바텀 낚시 기술'])}` });
                          if (size['해양 낚시 기술']) bonuses.push({ label: '해양 낚시 기술', value: `+${parseInt(size['해양 낚시 기술'])}` });
                          
                          // 특수 낚시 기법
                          if (size['매치 낚싯대로 낚시하기']) bonuses.push({ label: '매치 낚싯대로 낚시하기', value: `+${parseInt(size['매치 낚싯대로 낚시하기'])}` });
                          if (size['피더 낚싯대 낚시']) bonuses.push({ label: '피더 낚싯대 낚시', value: `+${parseInt(size['피더 낚싯대 낚시'])}` });
                          if (size['카프 낚싯대로 낚시하기']) bonuses.push({ label: '카프 낚싯대로 낚시하기', value: `+${parseInt(size['카프 낚싯대로 낚시하기'])}` });
                          if (size['저크베이트 낚싯대로 낚시하기']) bonuses.push({ label: '저크베이트 낚싯대로 낚시하기', value: `+${parseInt(size['저크베이트 낚싯대로 낚시하기'])}` });
                          if (size.지깅) bonuses.push({ label: '지깅', value: `+${parseInt(size.지깅)}` });
                          
                          // 채비 사용 보너스
                          if (size['고정줄 채비 사용']) bonuses.push({ label: '고정줄 채비 사용', value: `+${parseInt(size['고정줄 채비 사용'])}` });
                          if (size['슬라이더 채비 사용']) bonuses.push({ label: '슬라이더 채비 사용', value: `+${parseInt(size['슬라이더 채비 사용'])}` });
                          if (size['볼로네제 채비 사용']) bonuses.push({ label: '볼로네제 채비 사용', value: `+${parseInt(size['볼로네제 채비 사용'])}` });
                          if (size['매치 채비 사용']) bonuses.push({ label: '매치 채비 사용', value: `+${parseInt(size['매치 채비 사용'])}` });
                          if (size['카프헤어 채비 사용']) bonuses.push({ label: '카프헤어 채비 사용', value: `+${parseInt(size['카프헤어 채비 사용'])}` });
                          
                          // 루어 낚시 보너스
                          if (size['스피너와 스피너베이트로 낚시하기']) bonuses.push({ label: '스피너와 스피너베이트로 낚시하기', value: `+${parseInt(size['스피너와 스피너베이트로 낚시하기'])}` });
                          if (size['스푼으로 낚시하기']) bonuses.push({ label: '스푼으로 낚시하기', value: `+${parseInt(size['스푼으로 낚시하기'])}` });
                          if (size['워블러로 낚시하기']) bonuses.push({ label: '워블러로 낚시하기', value: `+${parseInt(size['워블러로 낚시하기'])}` });
                          
                          // 고급 채비 보너스
                          if (size['쓰리웨이 채비 사용']) bonuses.push({ label: '쓰리웨이 채비 사용', value: `+${parseInt(size['쓰리웨이 채비 사용'])}` });
                          if (size['텍사스 채비 사용']) bonuses.push({ label: '텍사스 채비 사용', value: `+${parseInt(size['텍사스 채비 사용'])}` });
                          if (size['드롭샷 채비 사용']) bonuses.push({ label: '드롭샷 채비 사용', value: `+${parseInt(size['드롭샷 채비 사용'])}` });
                          if (size['캐롤라이나 채비 사용']) bonuses.push({ label: '캐롤라이나 채비 사용', value: `+${parseInt(size['캐롤라이나 채비 사용'])}` });
                          if (size['와키 채비 사용']) bonuses.push({ label: '와키 채비 사용', value: `+${parseInt(size['와키 채비 사용'])}` });
                          if (size['저크베이트 채비 사용']) bonuses.push({ label: '저크베이트 채비 사용', value: `+${parseInt(size['저크베이트 채비 사용'])}` });
                          if (size['인라인 채비 사용']) bonuses.push({ label: '인라인 채비 사용', value: `+${parseInt(size['인라인 채비 사용'])}` });
                          
                          // 카프 채비 보너스
                          if (size['파터노스터 채비 사용']) bonuses.push({ label: '파터노스터 채비 사용', value: `+${parseInt(size['파터노스터 채비 사용'])}` });
                          if (size['루프 채비 사용']) bonuses.push({ label: '루프 채비 사용', value: `+${parseInt(size['루프 채비 사용'])}` });
                          if (size['클래식 헤어 채비 사용']) bonuses.push({ label: '클래식 헤어 채비 사용', value: `+${parseInt(size['클래식 헤어 채비 사용'])}` });
                          if (size['눈사람 채비 사용']) bonuses.push({ label: '눈사람 채비 사용', value: `+${parseInt(size['눈사람 채비 사용'])}` });
                          if (size['클래식 팝업 채비 사용']) bonuses.push({ label: '클래식 팝업 채비 사용', value: `+${parseInt(size['클래식 팝업 채비 사용'])}` });
                          if (size['힌지드 스티프 채비 사용']) bonuses.push({ label: '힌지드 스티프 채비 사용', value: `+${parseInt(size['힌지드 스티프 채비 사용'])}` });
                          if (size['초드 채비 사용']) bonuses.push({ label: '초드 채비 사용', value: `+${parseInt(size['초드 채비 사용'])}` });
                          if (size['구더기 스냅인 채비 사용']) bonuses.push({ label: '구더기 스냅인 채비 사용', value: `+${parseInt(size['구더기 스냅인 채비 사용'])}` });
                          if (size['클래식 메소드 채비 사용']) bonuses.push({ label: '클래식 메소드 채비 사용', value: `+${parseInt(size['클래식 메소드 채비 사용'])}` });
                          if (size['팝업 메소드 채비 사용']) bonuses.push({ label: '팝업 메소드 채비 사용', value: `+${parseInt(size['팝업 메소드 채비 사용'])}` });
                          if (size['지그-리그 채비 사용']) bonuses.push({ label: '지그-리그 채비 사용', value: `+${parseInt(size['지그-리그 채비 사용'])}` });
                          
                          // 해양 낚시 보너스
                          if (size['필커 지그']) bonuses.push({ label: '필커 지그', value: `+${parseInt(size['필커 지그'])}` });
                          if (size['마린 루어 지깅 채비']) bonuses.push({ label: '마린 루어 지깅 채비', value: `+${parseInt(size['마린 루어 지깅 채비'])}` });
                          if (size['필렛 지깅 채비']) bonuses.push({ label: '필렛 지깅 채비', value: `+${parseInt(size['필렛 지깅 채비'])}` });
                          if (size['플라잉 칼러 채비']) bonuses.push({ label: '플라잉 칼러 채비', value: `+${parseInt(size['플라잉 칼러 채비'])}` });
                          if (size['바텀 생미끼 채비']) bonuses.push({ label: '바텀 생미끼 채비', value: `+${parseInt(size['바텀 생미끼 채비'])}` });
                          if (size['자이언트 실리콘 지깅 채비']) bonuses.push({ label: '자이언트 실리콘 지깅 채비', value: `+${parseInt(size['자이언트 실리콘 지깅 채비'])}` });
                          if (size['자이언트 생미끼 지깅 채비']) bonuses.push({ label: '자이언트 생미끼 지깅 채비', value: `+${parseInt(size['자이언트 생미끼 지깅 채비'])}` });
                          if (size['마린 파터노스터 채비']) bonuses.push({ label: '마린 파터노스터 채비', value: `+${parseInt(size['마린 파터노스터 채비'])}` });
                          
                          // 기타 보너스
                          if (size['캐스팅 거리']) bonuses.push({ label: '캐스팅 거리', value: `+${parseInt(size['캐스팅 거리'])}%` });
                          
                          return bonuses.length > 0 ? (
                            <>
                              <p><strong>보너스:</strong></p>
                              <div className="mod-values">
                                {bonuses.map((bonus, bonusIndex) => (
                                  <div key={bonusIndex} className="mod-value">
                                    {bonus.label}: {bonus.value}
                                  </div>
                                ))}
                              </div>
                            </>
                          ) : null;
                        })()}
                      </div>
                      <div className="spec-group price">
                        {size.req_levels && (
                          <p><strong>필요 레벨:</strong> {size.req_levels}</p>
                        )}
                        {size.price_silver && (
                          <p><strong>실버:</strong> {size.price_silver.toLocaleString()}</p>
                        )}
                        {size.price_gold && (
                          <p><strong>골드:</strong> {size.price_gold.toLocaleString()}</p>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
});

// 낚싯대 비교 모달 컴포넌트 (크기별 상세 비교)
const RodCompareModal = React.memo(({ compareItems, onClose, onRemoveFromCompare }) => {
  const handleImageError = useCallback((filename) => {
    console.warn('Failed to load compare image:', filename);
  }, []);

  if (!compareItems || compareItems.length === 0) return null;

  // 각 속성에 대해 서로 다른 값이 있는지 확인하는 함수
  const hasVariation = (property, getValue) => {
    const values = compareItems.map(getValue);
    return new Set(values).size > 1;
  };

  // 값이 다른 경우 강조 클래스를 반환하는 함수
  const getHighlightClass = (property, getValue) => {
    return hasVariation(property, getValue) ? 'highlight-different' : '';
  };

  // 모든 항목이 '정보 없음'인지 확인하는 함수
  const isAllNoInfo = (getValue) => {
    return compareItems.every(item => {
      const value = getValue(item);
      return !value || value === '정보 없음' || value === '' || value === 0;
    });
  };

  // 행을 숨길지 결정하는 함수
  const getRowClass = (getValue) => {
    return isAllNoInfo(getValue) ? 'compare-row-hidden' : '';
  };

  return (
    <div className="compare-modal-overlay" onClick={onClose}>
      <div className="compare-modal" onClick={(e) => e.stopPropagation()}>
        <div className="compare-modal-header">
          <h2>낚싯대 크기별 상세 비교 ({compareItems.length}개)</h2>
          <button 
            className="modal-close-btn"
            onClick={onClose}
          >
            <X className="h-3 w-3" color="#6c757d" />
          </button>
        </div>
        
        <div className="compare-content">
          <div className="compare-table-container">
            <table className="compare-table">
              <thead>
                <tr>
                  <th className="spec-row-label">항목</th>
                  {compareItems.map((item, index) => (
                    <th key={`${item.rodId}-${item.sizeName}-${index}`}>
                      <div className="compare-header-item">
                        <button 
                          className="compare-remove-btn"
                          onClick={() => onRemoveFromCompare(item.rodId, item.sizeName)}
                          title="비교에서 제거"
                        >
                          <X className="h-3 w-3" color="white" />
                        </button>
                        <RodImage 
                          filename={item.rod.filename}
                          alt={item.rod.displayname}
                          className="compare-rod-image"
                          onError={() => handleImageError(item.rod.filename)}
                        />
                        <div className="compare-rod-name">{item.rod.displayname}</div>
                        <div className="compare-rod-type">{item.rod.manufacturer} - {item.sizeName}</div>
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="spec-row-label">낚싯대 타입</td>
                  {compareItems.map((item, index) => (
                    <td key={`type-${index}`} className={getHighlightClass('type', (item) => item.rod.rod_type)}>
                      {item.rod.rod_type}
                    </td>
                  ))}
                </tr>
                <tr className={getRowClass((item) => item.size.길이)}>
                  <td className="spec-row-label">길이</td>
                  {compareItems.map((item, index) => (
                    <td key={`length-${index}`} className={getHighlightClass('length', (item) => item.size.길이)}>
                      {item.size.길이 ? `${item.size.길이}m` : '정보 없음'}
                    </td>
                  ))}
                </tr>
                <tr className={getRowClass((item) => item.size.감도)}>
                  <td className="spec-row-label">감도</td>
                  {compareItems.map((item, index) => (
                    <td key={`sensitivity-${index}`} className={getHighlightClass('sensitivity', (item) => item.size.감도)}>
                      {item.size.감도 ? `${item.size.감도}/10` : '정보 없음'}
                    </td>
                  ))}
                </tr>
                <tr className={getRowClass((item) => item.size.액션)}>
                  <td className="spec-row-label">액션</td>
                  {compareItems.map((item, index) => (
                    <td key={`action-${index}`} className={getHighlightClass('action', (item) => item.size.액션)}>
                      {item.size.액션 || '정보 없음'}
                    </td>
                  ))}
                </tr>
                <tr className={getRowClass((item) => item.size.강도)}>
                  <td className="spec-row-label">강도</td>
                  {compareItems.map((item, index) => (
                    <td key={`strength-${index}`} className={getHighlightClass('strength', (item) => item.size.강도)}>
                      {item.size.강도 ? `${item.size.강도}/10` : '정보 없음'}
                    </td>
                  ))}
                </tr>
                <tr className={getRowClass((item) => item.size.파워)}>
                  <td className="spec-row-label">파워</td>
                  {compareItems.map((item, index) => (
                    <td key={`power-${index}`} className={getHighlightClass('power', (item) => item.size.파워)}>
                      {item.size.파워 || '정보 없음'}
                    </td>
                  ))}
                </tr>
                <tr className={getRowClass((item) => item.size['최소 테스트'] || item.size['최대 테스트'])}>
                  <td className="spec-row-label">테스트</td>
                  {compareItems.map((item, index) => (
                    <td key={`test-${index}`} className={getHighlightClass('test', (item) => `${item.size['최소 테스트']}-${item.size['최대 테스트']}`)}>
                      {item.size['최소 테스트'] && item.size['최대 테스트'] 
                        ? `${item.size['최소 테스트']}g - ${item.size['최대 테스트']}g`
                        : item.size['최소 테스트'] 
                          ? `${item.size['최소 테스트']}g`
                          : item.size['최대 테스트']
                            ? `${item.size['최대 테스트']}g`
                            : '정보 없음'
                      }
                    </td>
                  ))}
                </tr>
                <tr className={getRowClass((item) => item.size['최대 하중'])}>
                  <td className="spec-row-label">최대 하중</td>
                  {compareItems.map((item, index) => (
                    <td key={`max-load-${index}`} className={getHighlightClass('maxload', (item) => item.size['최대 하중'])}>
                      {item.size['최대 하중'] ? `${item.size['최대 하중']}kg` : '정보 없음'}
                    </td>
                  ))}
                </tr>
                <tr className={getRowClass((item) => item.size.무게)}>
                  <td className="spec-row-label">무게</td>
                  {compareItems.map((item, index) => (
                    <td key={`weight-${index}`} className={getHighlightClass('weight', (item) => item.size.무게)}>
                      {item.size.무게 ? `${item.size.무게}g` : '정보 없음'}
                    </td>
                  ))}
                </tr>
                
                {/* 낚시 기술 보너스 섹션 - 데이터가 있을 때만 표시 */}
                {(() => {
                  const bonusFields = [
                    '경험치', '캐스팅 거리', '찌 낚시 기술', '스핀 낚시 기술', '바텀 낚시 기술', '해양 낚시 기술',
                    '매치 낚싯대로 낚시하기', '피더 낚싯대 낚시', '카프 낚싯대로 낚시하기', '저크베이트 낚싯대로 낚시하기', '지깅',
                    '고정줄 채비 사용', '슬라이더 채비 사용', '볼로네제 채비 사용', '매치 채비 사용', '카프헤어 채비 사용',
                    '스피너와 스피너베이트로 낚시하기', '스푼으로 낚시하기', '워블러로 낚시하기',
                    '쓰리웨이 채비 사용', '텍사스 채비 사용', '드롭샷 채비 사용', '캐롤라이나 채비 사용', '와키 채비 사용',
                    '저크베이트 채비 사용', '인라인 채비 사용', '파터노스터 채비 사용', '루프 채비 사용',
                    '클래식 헤어 채비 사용', '눈사람 채비 사용', '클래식 팝업 채비 사용', '힌지드 스티프 채비 사용',
                    '초드 채비 사용', '구더기 스냅인 채비 사용', '클래식 메소드 채비 사용', '팝업 메소드 채비 사용',
                    '지그-리그 채비 사용', '필커 지그', '마린 루어 지깅 채비', '필렛 지깅 채비', '플라잉 칼러 채비',
                    '바텀 생미끼 채비', '자이언트 실리콘 지깅 채비', '자이언트 생미끼 지깅 채비', '마린 파터노스터 채비'
                  ];
                  
                  const hasBonusData = bonusFields.some(field => 
                    compareItems.some(item => {
                      const value = item.size[field];
                      return value !== null && value !== undefined && value !== '' && parseFloat(value) > 0;
                    })
                  );
                  
                  if (!hasBonusData) return null;
                  
                  return (
                    <>
                      <tr>
                        <td colSpan={compareItems.length + 1} className="section-header">
                          <h4>낚시 기술 보너스</h4>
                        </td>
                      </tr>
                <tr className={getRowClass((item) => item.size.경험치)}>
                  <td className="spec-row-label">경험치</td>
                  {compareItems.map((item, index) => (
                    <td key={`exp-${index}`} className={getHighlightClass('exp', (item) => item.size.경험치)}>
                      {item.size.경험치 ? `+${parseInt(item.size.경험치)}` : '정보 없음'}
                    </td>
                  ))}
                </tr>
                <tr className={getRowClass((item) => item.size['캐스팅 거리'])}>
                  <td className="spec-row-label">캐스팅 거리</td>
                  {compareItems.map((item, index) => (
                    <td key={`casting-${index}`} className={getHighlightClass('casting', (item) => item.size['캐스팅 거리'])}>
                      {item.size['캐스팅 거리'] ? `+${parseInt(item.size['캐스팅 거리'])}%` : '정보 없음'}
                    </td>
                  ))}
                </tr>
                <tr className={getRowClass((item) => item.size['찌 낚시 기술'])}>
                  <td className="spec-row-label">찌 낚시 기술</td>
                  {compareItems.map((item, index) => (
                    <td key={`float-${index}`} className={getHighlightClass('float', (item) => item.size['찌 낚시 기술'])}>
                      {item.size['찌 낚시 기술'] ? `+${parseInt(item.size['찌 낚시 기술'])}` : '정보 없음'}
                    </td>
                  ))}
                </tr>
                <tr className={getRowClass((item) => item.size['스핀 낚시 기술'])}>
                  <td className="spec-row-label">스핀 낚시 기술</td>
                  {compareItems.map((item, index) => (
                    <td key={`spin-${index}`} className={getHighlightClass('spin', (item) => item.size['스핀 낚시 기술'])}>
                      {item.size['스핀 낚시 기술'] ? `+${parseInt(item.size['스핀 낚시 기술'])}` : '정보 없음'}
                    </td>
                  ))}
                </tr>
                <tr className={getRowClass((item) => item.size['바텀 낚시 기술'])}>
                  <td className="spec-row-label">바텀 낚시 기술</td>
                  {compareItems.map((item, index) => (
                    <td key={`bottom-${index}`} className={getHighlightClass('bottom', (item) => item.size['바텀 낚시 기술'])}>
                      {item.size['바텀 낚시 기술'] ? `+${parseInt(item.size['바텀 낚시 기술'])}` : '정보 없음'}
                    </td>
                  ))}
                </tr>
                <tr className={getRowClass((item) => item.size['해양 낚시 기술'])}>
                  <td className="spec-row-label">해양 낚시 기술</td>
                  {compareItems.map((item, index) => (
                    <td key={`marine-${index}`} className={getHighlightClass('marine', (item) => item.size['해양 낚시 기술'])}>
                      {item.size['해양 낚시 기술'] ? `+${parseInt(item.size['해양 낚시 기술'])}` : '정보 없음'}
                    </td>
                  ))}
                </tr>
                <tr className={getRowClass((item) => item.size['매치 낚싯대로 낚시하기'])}>
                  <td className="spec-row-label">매치 낚싯대로 낚시하기</td>
                  {compareItems.map((item, index) => (
                    <td key={`match-${index}`} className={getHighlightClass('match', (item) => item.size['매치 낚싯대로 낚시하기'])}>
                      {item.size['매치 낚싯대로 낚시하기'] ? `+${parseInt(item.size['매치 낚싯대로 낚시하기'])}` : '정보 없음'}
                    </td>
                  ))}
                </tr>
                <tr className={getRowClass((item) => item.size['피더 낚싯대 낚시'])}>
                  <td className="spec-row-label">피더 낚싯대 낚시</td>
                  {compareItems.map((item, index) => (
                    <td key={`feeder-${index}`} className={getHighlightClass('feeder', (item) => item.size['피더 낚싯대 낚시'])}>
                      {item.size['피더 낚싯대 낚시'] ? `+${parseInt(item.size['피더 낚싯대 낚시'])}` : '정보 없음'}
                    </td>
                  ))}
                </tr>
                <tr className={getRowClass((item) => item.size['카프 낚싯대로 낚시하기'])}>
                  <td className="spec-row-label">카프 낚싯대로 낚시하기</td>
                  {compareItems.map((item, index) => (
                    <td key={`carp-${index}`} className={getHighlightClass('carp', (item) => item.size['카프 낚싯대로 낚시하기'])}>
                      {item.size['카프 낚싯대로 낚시하기'] ? `+${parseInt(item.size['카프 낚싯대로 낚시하기'])}` : '정보 없음'}
                    </td>
                  ))}
                </tr>
                <tr className={getRowClass((item) => item.size['저크베이트 낚싯대로 낚시하기'])}>
                  <td className="spec-row-label">저크베이트 낚싯대로 낚시하기</td>
                  {compareItems.map((item, index) => (
                    <td key={`jerk-${index}`} className={getHighlightClass('jerk', (item) => item.size['저크베이트 낚싯대로 낚시하기'])}>
                      {item.size['저크베이트 낚싯대로 낚시하기'] ? `+${parseInt(item.size['저크베이트 낚싯대로 낚시하기'])}` : '정보 없음'}
                    </td>
                  ))}
                </tr>
                <tr className={getRowClass((item) => item.size.지깅)}>
                  <td className="spec-row-label">지깅</td>
                  {compareItems.map((item, index) => (
                    <td key={`jigging-${index}`} className={getHighlightClass('jigging', (item) => item.size.지깅)}>
                      {item.size.지깅 ? `+${parseInt(item.size.지깅)}` : '정보 없음'}
                    </td>
                  ))}
                </tr>
                <tr className={getRowClass((item) => item.size['고정줄 채비 사용'])}>
                  <td className="spec-row-label">고정줄 채비 사용</td>
                  {compareItems.map((item, index) => (
                    <td key={`fixed-line-${index}`} className={getHighlightClass('fixed-line', (item) => item.size['고정줄 채비 사용'])}>
                      {item.size['고정줄 채비 사용'] ? `+${parseInt(item.size['고정줄 채비 사용'])}` : '정보 없음'}
                    </td>
                  ))}
                </tr>
                <tr className={getRowClass((item) => item.size['슬라이더 채비 사용'])}>
                  <td className="spec-row-label">슬라이더 채비 사용</td>
                  {compareItems.map((item, index) => (
                    <td key={`slider-${index}`} className={getHighlightClass('slider', (item) => item.size['슬라이더 채비 사용'])}>
                      {item.size['슬라이더 채비 사용'] ? `+${parseInt(item.size['슬라이더 채비 사용'])}` : '정보 없음'}
                    </td>
                  ))}
                </tr>
                <tr className={getRowClass((item) => item.size['볼로네제 채비 사용'])}>
                  <td className="spec-row-label">볼로네제 채비 사용</td>
                  {compareItems.map((item, index) => (
                    <td key={`bolognese-${index}`} className={getHighlightClass('bolognese', (item) => item.size['볼로네제 채비 사용'])}>
                      {item.size['볼로네제 채비 사용'] ? `+${parseInt(item.size['볼로네제 채비 사용'])}` : '정보 없음'}
                    </td>
                  ))}
                </tr>
                <tr className={getRowClass((item) => item.size['매치 채비 사용'])}>
                  <td className="spec-row-label">매치 채비 사용</td>
                  {compareItems.map((item, index) => (
                    <td key={`match-rig-${index}`} className={getHighlightClass('match-rig', (item) => item.size['매치 채비 사용'])}>
                      {item.size['매치 채비 사용'] ? `+${parseInt(item.size['매치 채비 사용'])}` : '정보 없음'}
                    </td>
                  ))}
                </tr>
                <tr className={getRowClass((item) => item.size['카프헤어 채비 사용'])}>
                  <td className="spec-row-label">카프헤어 채비 사용</td>
                  {compareItems.map((item, index) => (
                    <td key={`carp-hair-${index}`} className={getHighlightClass('carp-hair', (item) => item.size['카프헤어 채비 사용'])}>
                      {item.size['카프헤어 채비 사용'] ? `+${parseInt(item.size['카프헤어 채비 사용'])}` : '정보 없음'}
                    </td>
                  ))}
                </tr>
                <tr className={getRowClass((item) => item.size['스피너와 스피너베이트로 낚시하기'])}>
                  <td className="spec-row-label">스피너와 스피너베이트로 낚시하기</td>
                  {compareItems.map((item, index) => (
                    <td key={`spinner-${index}`} className={getHighlightClass('spinner', (item) => item.size['스피너와 스피너베이트로 낚시하기'])}>
                      {item.size['스피너와 스피너베이트로 낚시하기'] ? `+${parseInt(item.size['스피너와 스피너베이트로 낚시하기'])}` : '정보 없음'}
                    </td>
                  ))}
                </tr>
                <tr className={getRowClass((item) => item.size['스푼으로 낚시하기'])}>
                  <td className="spec-row-label">스푼으로 낚시하기</td>
                  {compareItems.map((item, index) => (
                    <td key={`spoon-${index}`} className={getHighlightClass('spoon', (item) => item.size['스푼으로 낚시하기'])}>
                      {item.size['스푼으로 낚시하기'] ? `+${parseInt(item.size['스푼으로 낚시하기'])}` : '정보 없음'}
                    </td>
                  ))}
                </tr>
                <tr className={getRowClass((item) => item.size['워블러로 낚시하기'])}>
                  <td className="spec-row-label">워블러로 낚시하기</td>
                  {compareItems.map((item, index) => (
                    <td key={`wobbler-${index}`} className={getHighlightClass('wobbler', (item) => item.size['워블러로 낚시하기'])}>
                      {item.size['워블러로 낚시하기'] ? `+${parseInt(item.size['워블러로 낚시하기'])}` : '정보 없음'}
                    </td>
                  ))}
                </tr>
                <tr className={getRowClass((item) => item.size['쓰리웨이 채비 사용'])}>
                  <td className="spec-row-label">쓰리웨이 채비 사용</td>
                  {compareItems.map((item, index) => (
                    <td key={`three-way-${index}`} className={getHighlightClass('three-way', (item) => item.size['쓰리웨이 채비 사용'])}>
                      {item.size['쓰리웨이 채비 사용'] ? `+${parseInt(item.size['쓰리웨이 채비 사용'])}` : '정보 없음'}
                    </td>
                  ))}
                </tr>
                <tr className={getRowClass((item) => item.size['텍사스 채비 사용'])}>
                  <td className="spec-row-label">텍사스 채비 사용</td>
                  {compareItems.map((item, index) => (
                    <td key={`texas-${index}`} className={getHighlightClass('texas', (item) => item.size['텍사스 채비 사용'])}>
                      {item.size['텍사스 채비 사용'] ? `+${parseInt(item.size['텍사스 채비 사용'])}` : '정보 없음'}
                    </td>
                  ))}
                </tr>
                <tr className={getRowClass((item) => item.size['드롭샷 채비 사용'])}>
                  <td className="spec-row-label">드롭샷 채비 사용</td>
                  {compareItems.map((item, index) => (
                    <td key={`dropshot-${index}`} className={getHighlightClass('dropshot', (item) => item.size['드롭샷 채비 사용'])}>
                      {item.size['드롭샷 채비 사용'] ? `+${parseInt(item.size['드롭샷 채비 사용'])}` : '정보 없음'}
                    </td>
                  ))}
                </tr>
                <tr className={getRowClass((item) => item.size['캐롤라이나 채비 사용'])}>
                  <td className="spec-row-label">캐롤라이나 채비 사용</td>
                  {compareItems.map((item, index) => (
                    <td key={`carolina-${index}`} className={getHighlightClass('carolina', (item) => item.size['캐롤라이나 채비 사용'])}>
                      {item.size['캐롤라이나 채비 사용'] ? `+${parseInt(item.size['캐롤라이나 채비 사용'])}` : '정보 없음'}
                    </td>
                  ))}
                </tr>
                <tr className={getRowClass((item) => item.size['와키 채비 사용'])}>
                  <td className="spec-row-label">와키 채비 사용</td>
                  {compareItems.map((item, index) => (
                    <td key={`wacky-${index}`} className={getHighlightClass('wacky', (item) => item.size['와키 채비 사용'])}>
                      {item.size['와키 채비 사용'] ? `+${parseInt(item.size['와키 채비 사용'])}` : '정보 없음'}
                    </td>
                  ))}
                </tr>
                <tr className={getRowClass((item) => item.size['저크베이트 채비 사용'])}>
                  <td className="spec-row-label">저크베이트 채비 사용</td>
                  {compareItems.map((item, index) => (
                    <td key={`jerkbait-rig-${index}`} className={getHighlightClass('jerkbait-rig', (item) => item.size['저크베이트 채비 사용'])}>
                      {item.size['저크베이트 채비 사용'] ? `+${parseInt(item.size['저크베이트 채비 사용'])}` : '정보 없음'}
                    </td>
                  ))}
                </tr>
                <tr className={getRowClass((item) => item.size['인라인 채비 사용'])}>
                  <td className="spec-row-label">인라인 채비 사용</td>
                  {compareItems.map((item, index) => (
                    <td key={`inline-${index}`} className={getHighlightClass('inline', (item) => item.size['인라인 채비 사용'])}>
                      {item.size['인라인 채비 사용'] ? `+${parseInt(item.size['인라인 채비 사용'])}` : '정보 없음'}
                    </td>
                  ))}
                </tr>
                <tr className={getRowClass((item) => item.size['파터노스터 채비 사용'])}>
                  <td className="spec-row-label">파터노스터 채비 사용</td>
                  {compareItems.map((item, index) => (
                    <td key={`paternoster-${index}`} className={getHighlightClass('paternoster', (item) => item.size['파터노스터 채비 사용'])}>
                      {item.size['파터노스터 채비 사용'] ? `+${parseInt(item.size['파터노스터 채비 사용'])}` : '정보 없음'}
                    </td>
                  ))}
                </tr>
                <tr className={getRowClass((item) => item.size['루프 채비 사용'])}>
                  <td className="spec-row-label">루프 채비 사용</td>
                  {compareItems.map((item, index) => (
                    <td key={`loop-${index}`} className={getHighlightClass('loop', (item) => item.size['루프 채비 사용'])}>
                      {item.size['루프 채비 사용'] ? `+${parseInt(item.size['루프 채비 사용'])}` : '정보 없음'}
                    </td>
                  ))}
                </tr>
                <tr className={getRowClass((item) => item.size['클래식 헤어 채비 사용'])}>
                  <td className="spec-row-label">클래식 헤어 채비 사용</td>
                  {compareItems.map((item, index) => (
                    <td key={`classic-hair-${index}`} className={getHighlightClass('classic-hair', (item) => item.size['클래식 헤어 채비 사용'])}>
                      {item.size['클래식 헤어 채비 사용'] ? `+${parseInt(item.size['클래식 헤어 채비 사용'])}` : '정보 없음'}
                    </td>
                  ))}
                </tr>
                <tr className={getRowClass((item) => item.size['눈사람 채비 사용'])}>
                  <td className="spec-row-label">눈사람 채비 사용</td>
                  {compareItems.map((item, index) => (
                    <td key={`snowman-${index}`} className={getHighlightClass('snowman', (item) => item.size['눈사람 채비 사용'])}>
                      {item.size['눈사람 채비 사용'] ? `+${parseInt(item.size['눈사람 채비 사용'])}` : '정보 없음'}
                    </td>
                  ))}
                </tr>
                <tr className={getRowClass((item) => item.size['클래식 팝업 채비 사용'])}>
                  <td className="spec-row-label">클래식 팝업 채비 사용</td>
                  {compareItems.map((item, index) => (
                    <td key={`classic-popup-${index}`} className={getHighlightClass('classic-popup', (item) => item.size['클래식 팝업 채비 사용'])}>
                      {item.size['클래식 팝업 채비 사용'] ? `+${parseInt(item.size['클래식 팝업 채비 사용'])}` : '정보 없음'}
                    </td>
                  ))}
                </tr>
                <tr className={getRowClass((item) => item.size['힌지드 스티프 채비 사용'])}>
                  <td className="spec-row-label">힌지드 스티프 채비 사용</td>
                  {compareItems.map((item, index) => (
                    <td key={`hinged-stiff-${index}`} className={getHighlightClass('hinged-stiff', (item) => item.size['힌지드 스티프 채비 사용'])}>
                      {item.size['힌지드 스티프 채비 사용'] ? `+${parseInt(item.size['힌지드 스티프 채비 사용'])}` : '정보 없음'}
                    </td>
                  ))}
                </tr>
                <tr className={getRowClass((item) => item.size['초드 채비 사용'])}>
                  <td className="spec-row-label">초드 채비 사용</td>
                  {compareItems.map((item, index) => (
                    <td key={`chod-${index}`} className={getHighlightClass('chod', (item) => item.size['초드 채비 사용'])}>
                      {item.size['초드 채비 사용'] ? `+${parseInt(item.size['초드 채비 사용'])}` : '정보 없음'}
                    </td>
                  ))}
                </tr>
                <tr className={getRowClass((item) => item.size['구더기 스냅인 채비 사용'])}>
                  <td className="spec-row-label">구더기 스냅인 채비 사용</td>
                  {compareItems.map((item, index) => (
                    <td key={`maggot-snapin-${index}`} className={getHighlightClass('maggot-snapin', (item) => item.size['구더기 스냅인 채비 사용'])}>
                      {item.size['구더기 스냅인 채비 사용'] ? `+${parseInt(item.size['구더기 스냅인 채비 사용'])}` : '정보 없음'}
                    </td>
                  ))}
                </tr>
                <tr className={getRowClass((item) => item.size['클래식 메소드 채비 사용'])}>
                  <td className="spec-row-label">클래식 메소드 채비 사용</td>
                  {compareItems.map((item, index) => (
                    <td key={`classic-method-${index}`} className={getHighlightClass('classic-method', (item) => item.size['클래식 메소드 채비 사용'])}>
                      {item.size['클래식 메소드 채비 사용'] ? `+${parseInt(item.size['클래식 메소드 채비 사용'])}` : '정보 없음'}
                    </td>
                  ))}
                </tr>
                <tr className={getRowClass((item) => item.size['팝업 메소드 채비 사용'])}>
                  <td className="spec-row-label">팝업 메소드 채비 사용</td>
                  {compareItems.map((item, index) => (
                    <td key={`popup-method-${index}`} className={getHighlightClass('popup-method', (item) => item.size['팝업 메소드 채비 사용'])}>
                      {item.size['팝업 메소드 채비 사용'] ? `+${parseInt(item.size['팝업 메소드 채비 사용'])}` : '정보 없음'}
                    </td>
                  ))}
                </tr>
                <tr className={getRowClass((item) => item.size['지그-리그 채비 사용'])}>
                  <td className="spec-row-label">지그-리그 채비 사용</td>
                  {compareItems.map((item, index) => (
                    <td key={`jig-rig-${index}`} className={getHighlightClass('jig-rig', (item) => item.size['지그-리그 채비 사용'])}>
                      {item.size['지그-리그 채비 사용'] ? `+${parseInt(item.size['지그-리그 채비 사용'])}` : '정보 없음'}
                    </td>
                  ))}
                </tr>
                <tr className={getRowClass((item) => item.size['필커 지그'])}>
                  <td className="spec-row-label">필커 지그</td>
                  {compareItems.map((item, index) => (
                    <td key={`pilker-jig-${index}`} className={getHighlightClass('pilker-jig', (item) => item.size['필커 지그'])}>
                      {item.size['필커 지그'] ? `+${parseInt(item.size['필커 지그'])}` : '정보 없음'}
                    </td>
                  ))}
                </tr>
                <tr className={getRowClass((item) => item.size['마린 루어 지깅 채비'])}>
                  <td className="spec-row-label">마린 루어 지깅 채비</td>
                  {compareItems.map((item, index) => (
                    <td key={`marine-lure-jigging-${index}`} className={getHighlightClass('marine-lure-jigging', (item) => item.size['마린 루어 지깅 채비'])}>
                      {item.size['마린 루어 지깅 채비'] ? `+${parseInt(item.size['마린 루어 지깅 채비'])}` : '정보 없음'}
                    </td>
                  ))}
                </tr>
                <tr className={getRowClass((item) => item.size['필렛 지깅 채비'])}>
                  <td className="spec-row-label">필렛 지깅 채비</td>
                  {compareItems.map((item, index) => (
                    <td key={`fillet-jigging-${index}`} className={getHighlightClass('fillet-jigging', (item) => item.size['필렛 지깅 채비'])}>
                      {item.size['필렛 지깅 채비'] ? `+${parseInt(item.size['필렛 지깅 채비'])}` : '정보 없음'}
                    </td>
                  ))}
                </tr>
                <tr className={getRowClass((item) => item.size['플라잉 칼러 채비'])}>
                  <td className="spec-row-label">플라잉 칼러 채비</td>
                  {compareItems.map((item, index) => (
                    <td key={`flying-collar-${index}`} className={getHighlightClass('flying-collar', (item) => item.size['플라잉 칼러 채비'])}>
                      {item.size['플라잉 칼러 채비'] ? `+${parseInt(item.size['플라잉 칼러 채비'])}` : '정보 없음'}
                    </td>
                  ))}
                </tr>
                <tr className={getRowClass((item) => item.size['바텀 생미끼 채비'])}>
                  <td className="spec-row-label">바텀 생미끼 채비</td>
                  {compareItems.map((item, index) => (
                    <td key={`bottom-live-bait-${index}`} className={getHighlightClass('bottom-live-bait', (item) => item.size['바텀 생미끼 채비'])}>
                      {item.size['바텀 생미끼 채비'] ? `+${parseInt(item.size['바텀 생미끼 채비'])}` : '정보 없음'}
                    </td>
                  ))}
                </tr>
                <tr className={getRowClass((item) => item.size['자이언트 실리콘 지깅 채비'])}>
                  <td className="spec-row-label">자이언트 실리콘 지깅 채비</td>
                  {compareItems.map((item, index) => (
                    <td key={`giant-silicon-jigging-${index}`} className={getHighlightClass('giant-silicon-jigging', (item) => item.size['자이언트 실리콘 지깅 채비'])}>
                      {item.size['자이언트 실리콘 지깅 채비'] ? `+${parseInt(item.size['자이언트 실리콘 지깅 채비'])}` : '정보 없음'}
                    </td>
                  ))}
                </tr>
                <tr className={getRowClass((item) => item.size['자이언트 생미끼 지깅 채비'])}>
                  <td className="spec-row-label">자이언트 생미끼 지깅 채비</td>
                  {compareItems.map((item, index) => (
                    <td key={`giant-live-bait-jigging-${index}`} className={getHighlightClass('giant-live-bait-jigging', (item) => item.size['자이언트 생미끼 지깅 채비'])}>
                      {item.size['자이언트 생미끼 지깅 채비'] ? `+${parseInt(item.size['자이언트 생미끼 지깅 채비'])}` : '정보 없음'}
                    </td>
                  ))}
                </tr>
                <tr className={getRowClass((item) => item.size['마린 파터노스터 채비'])}>
                  <td className="spec-row-label">마린 파터노스터 채비</td>
                  {compareItems.map((item, index) => (
                    <td key={`marine-paternoster-${index}`} className={getHighlightClass('marine-paternoster', (item) => item.size['마린 파터노스터 채비'])}>
                      {item.size['마린 파터노스터 채비'] ? `+${parseInt(item.size['마린 파터노스터 채비'])}` : '정보 없음'}
                    </td>
                  ))}
                </tr>
                    </>
                  );
                })()}

                {/* 가격 정보 - 데이터가 있을 때만 표시 */}
                {(() => {
                  const priceFields = ['req_levels', 'price_silver', 'price_gold'];
                  
                  const hasPriceData = priceFields.some(field => 
                    compareItems.some(item => {
                      const value = item.size[field];
                      return value !== null && value !== undefined && value !== '' && parseFloat(value) > 0;
                    })
                  );
                  
                  if (!hasPriceData) return null;
                  
                  return (
                    <>
                      <tr>
                        <td colSpan={compareItems.length + 1} className="section-header">
                          <h4>가격 정보</h4>
                        </td>
                      </tr>
                <tr className={getRowClass((item) => item.size.req_levels)}>
                  <td className="spec-row-label">필요 레벨</td>
                  {compareItems.map((item, index) => (
                    <td key={`level-${index}`} className={getHighlightClass('level', (item) => item.size.req_levels)}>
                      {item.size.req_levels || '정보 없음'}
                    </td>
                  ))}
                </tr>
                <tr className={getRowClass((item) => item.size.price_silver)}>
                  <td className="spec-row-label">가격 (실버)</td>
                  {compareItems.map((item, index) => (
                    <td key={`price-silver-${index}`} className={getHighlightClass('pricesilver', (item) => item.size.price_silver)}>
                      {item.size.price_silver ? `${item.size.price_silver.toLocaleString()} 실버` : '정보 없음'}
                    </td>
                  ))}
                </tr>
                <tr className={getRowClass((item) => item.size.price_gold)}>
                  <td className="spec-row-label">가격 (골드)</td>
                  {compareItems.map((item, index) => (
                    <td key={`price-gold-${index}`} className={getHighlightClass('pricegold', (item) => item.size.price_gold)}>
                      {item.size.price_gold ? `${item.size.price_gold} 골드` : '정보 없음'}
                    </td>
                  ))}
                </tr>
                    </>
                  );
                })()}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
});

// 낚싯대 타일 컴포넌트 - 리렌더링 최적화를 위해 분리
const RodTile = React.memo(({ rod, onRodClick, rodHasBonus }) => {
    const handleClick = useCallback(() => {
        onRodClick(rod.id);
    }, [rod.id, onRodClick]);

    const handleImageError = useCallback(() => {
        console.warn('Failed to load tile image:', rod.filename);
    }, [rod.filename]);

    return (
        <div 
            className="rod-tile"
            onClick={handleClick}
        >
            <RodImage 
                filename={rod.filename}
                alt={rod.displayname}
                className="rod-tile-image"
                onError={handleImageError}
            />
            <div className="rod-tile-info">
                <div className="rod-tile-type">{rod.rod_type}</div>
                <div className="rod-tile-manufacturer">{rod.manufacturer}</div>
                <div className={`rod-tile-name ${rodHasBonus(rod) ? 'has-bonus' : ''}`}>{rod.displayname}</div>
                <div className="rod-tile-specs">
                    <div className="spec-item">
                        <span className="spec-label">길이:</span>
                        <span className="spec-value">
                            {rod.min_length && rod.max_length 
                                ? `${rod.min_length}m ~ ${rod.max_length}m`
                                : '정보 없음'}
                        </span>
                    </div>
                    <div className="spec-item">
                        <span className="spec-label">테스트:</span>
                        <span className="spec-value">
                            {rod.min_test && rod.max_test 
                                ? `${rod.min_test}g ~ ${rod.max_test}g`
                                : '정보 없음'}
                        </span>
                    </div>
                    <div className="spec-item">
                        <span className="spec-label">파워:</span>
                        <span className="spec-value">
                            {rod.power_range || '정보 없음'}
                        </span>
                    </div>
                    <div className="spec-item">
                        <span className="spec-label">액션:</span>
                        <span className="spec-value">
                            {rod.action_range || '정보 없음'}
                        </span>
                    </div>
                    <div className="spec-item price">
                        <span className="spec-label">가격:</span>
                        <span className="spec-value">
                            {rod.min_price_silver && (
                                <span className="price-silver">
                                    최소 {rod.min_price_silver.toLocaleString()} 실버
                                </span>
                            )}
                            {rod.min_price_gold && (
                                <span className="price-gold">
                                    최소 {rod.min_price_gold.toLocaleString()} 골드
                                </span>
                            )}
                            {!rod.min_price_silver && !rod.min_price_gold && '정보 없음'}
                        </span>
                    </div>
                </div>
            </div>
        </div>
    );
});

const Page1 = () => {
    const [rods, setRods] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [selectedRod, setSelectedRod] = useState(null);
    const [modalLoading, setModalLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [sortBy, setSortBy] = useState('name');
    const [selectedType, setSelectedType] = useState('all');
    const [compareList, setCompareList] = useState([]);
    const [showCompareModal, setShowCompareModal] = useState(false);
    const [buttonLayout, setButtonLayout] = useState([]);
    const [searchPlaceholder, setSearchPlaceholder] = useState('낚싯대 이름, 제조사, 타입, 크기명으로 검색...');
    const [viewMode, setViewMode] = useState(() => {
        // 로컬 스토리지에서 마지막 뷰 모드를 가져오기
        try {
            const savedViewMode = localStorage.getItem('rodsViewMode');
            return savedViewMode === 'table' ? 'table' : 'grid';
        } catch (error) {
            console.warn('로컬 스토리지에서 뷰 모드를 가져오는데 실패했습니다:', error);
            return 'grid';
        }
    });
    const [rodSizes, setRodSizes] = useState([]); // 로드 크기 데이터
    
    // 버튼 컨테이너 ref
    const buttonContainerRef = useRef(null);
    const tempButtonsRef = useRef(null);
    const searchInputRef = useRef(null);

    // 낚싯대 타입 목록
    const rodTypes = useMemo(() => [
        { key: 'all', label: '전체' },
        { key: '텔레스코픽', label: '텔레스코픽' },
        { key: '볼로네제', label: '볼로네제' },
        { key: '매치', label: '매치' },
        { key: '스피닝', label: '스피닝' },
        { key: '캐스팅', label: '캐스팅' },
        { key: '저크', label: '저크' },
        { key: '피더', label: '피더' },
        { key: '피커', label: '피커' },
        { key: '카프', label: '카프' },
        { key: '필커', label: '필커' },
        { key: '보트', label: '보트' },
    ], []);

    // 버튼들을 균형 잡힌 줄로 배치하는 함수 (실제 너비 측정 기반)
    const createBalancedButtonRows = useCallback((buttons) => {
        if (!buttonContainerRef.current || !tempButtonsRef.current) {
            // 초기 로딩 시 기본 레이아웃 반환
            return buttons.length <= 6 ? [buttons] : [buttons.slice(0, 6), buttons.slice(6)];
        }

        const containerWidth = buttonContainerRef.current.offsetWidth;
        const availableWidth = containerWidth * 0.9; // 90% 사용
        
        // 임시로 모든 버튼을 렌더링하여 실제 너비 측정
        const tempContainer = tempButtonsRef.current;
        tempContainer.innerHTML = '';
        tempContainer.style.visibility = 'hidden';
        tempContainer.style.position = 'absolute';
        tempContainer.style.top = '-9999px';
        tempContainer.style.display = 'flex';
        tempContainer.style.gap = '10px';
        
        const buttonWidths = buttons.map(button => {
            const tempButton = document.createElement('button');
            tempButton.className = 'type-button';
            tempButton.textContent = button.label;
            tempButton.style.visibility = 'hidden';
            tempContainer.appendChild(tempButton);
            const width = tempButton.offsetWidth;
            return { ...button, width };
        });
        
        // 정리
        tempContainer.innerHTML = '';
        
        // 자연스러운 줄바꿈으로 기본 레이아웃 생성
        const naturalLayout = [];
        let currentRow = [];
        let currentRowWidth = 0;
        
        for (const button of buttonWidths) {
            const buttonWidthWithGap = button.width + (currentRow.length > 0 ? 10 : 0);
            
            if (currentRowWidth + buttonWidthWithGap <= availableWidth || currentRow.length === 0) {
                currentRow.push(button);
                currentRowWidth += buttonWidthWithGap;
            } else {
                naturalLayout.push([...currentRow]);
                currentRow = [button];
                currentRowWidth = button.width;
            }
        }
        
        if (currentRow.length > 0) {
            naturalLayout.push(currentRow);
        }
        
        // 줄 간 버튼 수 균형 맞추기
        const balancedLayout = balanceRows(naturalLayout);
        
        // 최종 레이아웃을 원래 형태로 변환
        return balancedLayout.map(row => row.map(item => ({ key: item.key, label: item.label })));
    }, []);

    // 줄 간 버튼 수를 균형있게 조정하는 함수
    const balanceRows = (layout) => {
        if (layout.length <= 1) return layout;
        
        const balanced = JSON.parse(JSON.stringify(layout)); // 깊은 복사
        let improved = true;
        
        while (improved) {
            improved = false;
            
            for (let i = 0; i < balanced.length - 1; i++) {
                const currentRow = balanced[i];
                const nextRow = balanced[i + 1];
                const diff = currentRow.length - nextRow.length;
                
                // 차이가 2 이상인 경우 버튼 이동
                if (diff >= 2) {
                    // 현재 줄에서 마지막 버튼을 다음 줄로 이동
                    const buttonToMove = currentRow.pop();
                    nextRow.unshift(buttonToMove);
                    improved = true;
                } else if (diff <= -2) {
                    // 다음 줄에서 첫 번째 버튼을 현재 줄로 이동
                    const buttonToMove = nextRow.shift();
                    currentRow.push(buttonToMove);
                    improved = true;
                }
            }
        }
        
        return balanced;
    };

    // ESC 키로 모달 닫기 (우선순위: RodCompareModal > RodModal)
    useEffect(() => {
        const handleKeyDown = (event) => {
            if (event.key === 'Escape') {
                if (showCompareModal) {
                    setShowCompareModal(false);
                } else if (selectedRod) {
                    setSelectedRod(null);
                }
            }
        };

        document.addEventListener('keydown', handleKeyDown);
        return () => {
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [showCompareModal, selectedRod]);

    // 컨테이너 크기 변화 감지 및 레이아웃 재계산
    useEffect(() => {
        const calculateLayout = () => {
            if (buttonContainerRef.current) {
                const newLayout = createBalancedButtonRows(rodTypes);
                setButtonLayout(newLayout);
            }
        };

        // 초기 계산
        const timer = setTimeout(calculateLayout, 100);

        // ResizeObserver로 크기 변화 감지
        const resizeObserver = new ResizeObserver((entries) => {
            for (let entry of entries) {
                if (entry.target === buttonContainerRef.current) {
                    calculateLayout();
                }
            }
        });

        if (buttonContainerRef.current) {
            resizeObserver.observe(buttonContainerRef.current);
        }

        // 윈도우 리사이즈 이벤트도 감지
        const handleResize = () => {
            setTimeout(calculateLayout, 50);
        };
        window.addEventListener('resize', handleResize);

        return () => {
            clearTimeout(timer);
            resizeObserver.disconnect();
            window.removeEventListener('resize', handleResize);
        };
    }, [createBalancedButtonRows, rodTypes]);

    // 검색 입력창 크기에 따른 placeholder 동적 조정
    useEffect(() => {
        const updatePlaceholder = () => {
            if (searchInputRef.current) {
                const inputWidth = searchInputRef.current.offsetWidth;
                
                if (inputWidth < 300) {
                    setSearchPlaceholder('검색...');
                } else if (inputWidth < 400) {
                    setSearchPlaceholder('낚싯대 검색...');
                } else if (inputWidth < 500) {
                    setSearchPlaceholder('낚싯대 이름, 제조사 검색...');
                } else {
                    setSearchPlaceholder('낚싯대 이름, 제조사, 타입, 크기명으로 검색');
                }
            }
        };

        // 초기 실행
        const timer = setTimeout(updatePlaceholder, 100);

        // ResizeObserver로 검색 입력창 크기 변화 감지
        const resizeObserver = new ResizeObserver(() => {
            updatePlaceholder();
        });

        if (searchInputRef.current) {
            resizeObserver.observe(searchInputRef.current);
        }

        // 윈도우 리사이즈 이벤트 감지
        const handleResize = () => {
            setTimeout(updatePlaceholder, 50);
        };
        window.addEventListener('resize', handleResize);

        return () => {
            clearTimeout(timer);
            resizeObserver.disconnect();
            window.removeEventListener('resize', handleResize);
        };
    }, []);

    // 낚싯대 데이터 로드
    useEffect(() => {
        const fetchRods = async () => {
            setLoading(true);
            setError('');
        try {
          const response = await fetch(`/api/rods`);
                if (!response.ok) throw new Error('낚싯대 데이터를 불러올 수 없습니다');
                const data = await response.json();
                setRods(data);
            } catch (err) {
                setError(err.message);
            }
            setLoading(false);
        };

        const fetchRodSizes = async () => {
            try {
                const res = await fetch('/api/rod-sizes');
                if (!res.ok) throw new Error('로드 크기 데이터를 불러올 수 없습니다');
                const data = await res.json();
                setRodSizes(data);
            } catch (e) {
                console.error('Error fetching rod sizes:', e);
                setRodSizes([]);
            }
        };

        fetchRods();
        fetchRodSizes();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const handleRodClick = useCallback(async (rodId) => {
        setModalLoading(true);
    try {
      const res = await fetch(`/api/rods/detail/${rodId}`);
            if (!res.ok) throw new Error('상세 정보를 불러올 수 없습니다');
            const data = await res.json();
            setSelectedRod(data);
        } catch (e) {
            console.error('Error fetching rod detail:', e);
            alert('낚싯대 상세 정보를 불러오는데 실패했습니다.');
        }
        setModalLoading(false);
    }, []);

    // 비교 목록에 크기별 낚싯대 추가
    const handleAddToCompare = useCallback((rod, size) => {
        setCompareList(prev => {
            const exists = prev.find(item => item.rodId === rod.id && item.sizeName === size.이름);
            if (exists) {
                return prev; // 이미 있으면 그대로
            }
            
            // 최대 4개까지만 허용
            if (prev.length >= 4) {
                alert('최대 4개까지만 비교할 수 있습니다.');
                return prev;
            }
            
            return [...prev, {
                rodId: rod.id,
                sizeName: size.이름,
                rod: rod,
                size: size
            }];
        });
    }, []);

    // 비교 목록에서 특정 크기의 낚싯대 제거
    const handleRemoveFromCompare = useCallback((rodId, sizeName) => {
        setCompareList(prev => prev.filter(item => !(item.rodId === rodId && item.sizeName === sizeName)));
    }, []);

    // 비교 모달 열기
    const handleCompareClick = useCallback(() => {
        if (compareList.length < 2) {
            alert('비교하려면 최소 2개의 낚싯대를 선택해주세요.');
            return;
        }
        setShowCompareModal(true);
    }, [compareList.length]);

    // 비교 목록 초기화
    const handleClearCompare = useCallback(() => {
        setCompareList([]);
    }, []);

    // 모달 닫기 핸들러들
    const handleCloseRodModal = useCallback(() => {
        setSelectedRod(null);
    }, []);

    const handleCloseCompareModal = useCallback(() => {
        setShowCompareModal(false);
    }, []);

    // 뷰 모드 변경 및 로컬 스토리지 저장
    const handleViewModeChange = (newViewMode) => {
        setViewMode(newViewMode);
        try {
            localStorage.setItem('rodsViewMode', newViewMode);
        } catch (error) {
            console.warn('로컬 스토리지에 뷰 모드를 저장하는데 실패했습니다:', error);
        }
    };

    // 낚싯대에 보너스가 있는지 확인하는 함수 (서버에서 계산된 값 사용)
    const rodHasBonus = (rod) => {
        return rod.has_bonus === 1;
    };

    // 검색, 타입 필터링 및 정렬 로직 - useMemo로 최적화
    const filteredRods = useMemo(() => {
        return rods
            .filter(rod => {
                // 타입 필터링
                if (selectedType !== 'all' && rod.rod_type !== selectedType) {
                    return false;
                }
                // 검색어 필터링
                if (!searchTerm) return true;
                const term = searchTerm.toLowerCase();
                return (
                    rod.displayname?.toLowerCase().includes(term) ||
                    rod.manufacturer?.toLowerCase().includes(term) ||
                    rod.rod_type?.toLowerCase().includes(term) ||
                    rod.note?.toLowerCase().includes(term) ||
                    rod.size_names?.toLowerCase().includes(term)
                );
            })
            .sort((a, b) => {
                switch (sortBy) {
                    case 'name':
                        return a.displayname?.localeCompare(b.displayname) || 0;
                    case 'manufacturer':
                        return a.manufacturer?.localeCompare(b.manufacturer) || 0;
                    case 'type':
                        return a.rod_type?.localeCompare(b.rod_type) || 0;
                    case 'price_asc':
                        // 가격이 없거나 0인 경우 맨 마지막에 표시
                        const priceA = a.min_price_silver || 0;
                        const priceB = b.min_price_silver || 0;
                        if (priceA === 0 && priceB > 0) return 1;
                        if (priceB === 0 && priceA > 0) return -1;
                        return priceA - priceB;
                    case 'price_desc':
                        // 가격이 없거나 0인 경우 맨 마지막에 표시
                        const priceDescA = a.min_price_silver || 0;
                        const priceDescB = b.min_price_silver || 0;
                        if (priceDescA === 0 && priceDescB > 0) return 1;
                        if (priceDescB === 0 && priceDescA > 0) return -1;
                        return priceDescB - priceDescA;
                    default:
                        return 0;
                }
            });
    }, [rods, selectedType, searchTerm, sortBy]);

    return (
        <>
            <h1 className="page-title">낚싯대 정보</h1>
            
            {/* 검색 기능과 정렬 */}
            <div className="search-and-sort-container">
                <div className="search-container" style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    maxWidth: '500px',
                    margin: '0 auto'
                }}>
                    <input 
                        ref={searchInputRef}
                        type="text"
                        placeholder={searchPlaceholder}
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="search-input"
                    />
                    {searchTerm && (
                        <button 
                            onClick={() => setSearchTerm('')}
                            className="search-clear-btn"
                        >
                            ×
                        </button>
                    )}
                </div>
                
                {/* 정렬 선택 */}
                <div className="sort-container">
                    <label htmlFor="sort-select">정렬:</label>
                    <select 
                        id="sort-select"
                        value={sortBy} 
                        onChange={(e) => setSortBy(e.target.value)}
                        className="sort-select"
                    >
                        <option value="name">이름순</option>
                        <option value="manufacturer">제조사순</option>
                        <option value="type">타입순</option>
                        <option value="price_asc">가격 낮은순</option>
                        <option value="price_desc">가격 높은순</option>
                    </select>
                </div>
                
                {/* 뷰 모드 전환 버튼 */}
                <div className="view-mode-container">
                    <label>보기:</label>
                    <div className="view-mode-buttons">
                        <button 
                            className={`view-mode-btn ${viewMode === 'grid' ? 'active' : ''}`}
                            onClick={() => handleViewModeChange('grid')}
                            title="그리드 보기"
                        >
                            ⊞
                        </button>
                        <button 
                            className={`view-mode-btn ${viewMode === 'table' ? 'active' : ''}`}
                            onClick={() => handleViewModeChange('table')}
                            title="테이블 보기"
                        >
                            ≡
                        </button>
                    </div>
                </div>
            </div>

            {/* 낚싯대 타입 선택 버튼 */}
            <div className="reel-type-buttons-container" ref={buttonContainerRef}>
                {/* 임시 버튼 측정용 컨테이너 */}
                <div ref={tempButtonsRef} style={{ position: 'absolute', visibility: 'hidden', top: '-9999px' }}></div>
                
                {buttonLayout.length > 0 ? (
                    buttonLayout.map((row, rowIndex) => (
                        <div key={rowIndex} className="reel-type-buttons-row">
                            {row.map(type => (
                                <button 
                                    key={type.key}
                                    className={`type-button ${selectedType === type.key ? 'active' : ''}`}
                                    onClick={() => setSelectedType(type.key)}
                                >
                                    {type.label}
                                </button>
                            ))}
                        </div>
                    ))
                ) : (
                    // 초기 로딩 시 기본 레이아웃
                    <div className="reel-type-buttons-row">
                        {rodTypes.map(type => (
                            <button 
                                key={type.key}
                                className={`type-button ${selectedType === type.key ? 'active' : ''}`}
                                onClick={() => setSelectedType(type.key)}
                            >
                                {type.label}
                            </button>
                        ))}
                    </div>
                )}
            </div>
            <div className="page-content">
                {loading ? (
                    <div className="spinner-container">
                        <LoadingSpinner height={80} width={80} color="#4fa94d" visible={true} />
                    </div>
                ) : error ? (
                    <div className="error-message">
                        <p>{error}</p>
                        <button onClick={() => window.location.reload()}>다시 시도</button>
                    </div>
                ) : (
                    <>
                        {/* 검색 결과 개수 표시 */}
                        {searchTerm && (
                            <div className="search-results-info">
                                <p>'{searchTerm}' 검색 결과: {filteredRods.length}개</p>
                            </div>
                        )}
                        
                        {filteredRods.length === 0 ? (
                            <div className="no-data">
                                <p>{searchTerm ? '검색 결과가 없습니다.' : '해당 타입의 낚싯대 데이터가 없습니다.'}</p>
                            </div>
                        ) : (
                            <>
                                {/* 뷰 모드에 따른 렌더링 */}
                                {viewMode === 'table' ? (
                                    <RodTableView 
                                        rods={filteredRods}
                                        rodSizes={rodSizes}
                                        searchTerm={searchTerm}
                                    />
                                ) : (
                                    <div className="rod-grid">
                                        {filteredRods.map((rod) => (
                                            <RodTile 
                                                key={rod.id}
                                                rod={rod}
                                                onRodClick={handleRodClick}
                                                rodHasBonus={rodHasBonus}
                                            />
                                        ))}
                                    </div>
                                )}
                            </>
                        )}
                    </>
                )}
            </div>
            
            {modalLoading && (
                <div className="modal-overlay">
                    <LoadingSpinner height={80} width={80} color="#4fa94d" visible={true} />
                </div>
            )}
            
            {selectedRod && (
                <RodModal 
                    rod={selectedRod} 
                    onClose={handleCloseRodModal}
                    onAddToCompare={handleAddToCompare}
                    onRemoveFromCompare={handleRemoveFromCompare}
                    compareList={compareList}
                />
            )}
            
            {/* 비교 기능 UI */}
            {compareList.length > 0 && (
                <div className="compare-controls">
                    <div className="compare-info">
                        <span>선택된 낚싯대: {compareList.length}개</span>
                        <div className="compare-list">
                            {compareList.map(item => (
                                <span key={`${item.rodId}-${item.sizeName}`} className="compare-item">
                                    {item.rod.displayname} {item.sizeName}
                                </span>
                            ))}
                        </div>
                    </div>
                    <div className="compare-buttons">
                        <button 
                            className="compare-btn primary"
                            onClick={handleCompareClick}
                            disabled={compareList.length < 2}
                        >
                            비교하기 ({compareList.length})
                        </button>
                        <button 
                            className="compare-btn secondary"
                            onClick={handleClearCompare}
                        >
                            선택 초기화
                        </button>
                    </div>
                </div>
            )}

            {/* 낚싯대 비교 모달 */}
            {showCompareModal && (
                <RodCompareModal 
                    compareItems={compareList}
                    onClose={handleCloseCompareModal}
                    onRemoveFromCompare={handleRemoveFromCompare}
                />
            )}

            <Comments pageName="rod" />
        </>
    );
};

export default Page1;